import React, { type FC, useCallback, useMemo } from "react";
import { useActionSheet, useIsOverlayOpenByKeys, useWindows } from "@app/overlay";
import { useBoAvOperations, type ApiOmpBoAvOperation } from "@src/api/bo";
import { CardHeader } from "@shared/ui/card-header";
import { CardLoader } from "@src/components/card-loader";
import {
    BusinessBoStateValue,
    BusinessDivisionValue,
    BusinessEnterpriseValue,
    formatBoStateText,
    formatDivisionText,
    formatEnterpriseText,
} from "@src/components/business-data-display";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { DetailList } from "@shared/ui/detail-row";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import type { OnsideInvoice } from "../../../../api/onsideInvoices";
import { StockFinishedGoodsShipmentContentsList } from "./components/StockFinishedGoodsShipmentContentsList/StockFinishedGoodsShipmentContentsList";
import { StockFinishedGoodsShipmentScanPlaceholder } from "./components/StockFinishedGoodsShipmentScanPlaceholder/StockFinishedGoodsShipmentScanPlaceholder";
import { useStockFinishedGoodsShipmentCardQuery, useStockFinishedGoodsShipmentContentsQuery } from "./hooks";
import { useStockFinishedGoodsShipmentCardMutations } from "./useStockFinishedGoodsShipmentCardMutations";
import { stockFinishedGoodsShipmentCardWindows } from "./stockFinishedGoodsShipmentCard.windows";
import "./StockFinishedGoodsShipmentCard.scss";

const STOCK_FINISHED_GOODS_SHIPMENT_CARD_SCAN_PAUSE_WINDOW_KEYS = [stockFinishedGoodsShipmentCardWindows.content.key];

/** Карточка отгрузки готовой продукции со склада СГП по НС. */
export const StockFinishedGoodsShipmentCard: FC = () => {
    const invoiceCode = useRequiredNumberParam("code", "код НС");
    const windows = useWindows();
    const actionSheet = useActionSheet();

    const cardQuery = useStockFinishedGoodsShipmentCardQuery(invoiceCode);
    const contentsQuery = useStockFinishedGoodsShipmentContentsQuery(invoiceCode);
    const invoice = cardQuery.data;
    const avOperationsQuery = useBoAvOperations(invoice?.Code, invoice != null);
    const isCardScannerSuppressedByWindow = useIsOverlayOpenByKeys(
        STOCK_FINISHED_GOODS_SHIPMENT_CARD_SCAN_PAUSE_WINDOW_KEYS,
    );

    const mutations = useStockFinishedGoodsShipmentCardMutations({
        onContentRefetch: (contentCode) => contentsQuery.refetchContentByCode(contentCode),
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void contentsQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const buildBoOperationParams = useCallback((operation: ApiOmpBoAvOperation, currentInvoice: OnsideInvoice) => {
        return {
            operation,
            boCode: currentInvoice.Code,
            documentLabel: `НС №${currentInvoice.Num}`,
            fields: [
                { label: "Склад", value: formatEmptyValue(formatDivisionText(currentInvoice.WsSender)) },
                {
                    label: "Контрагент-получатель",
                    value: formatEmptyValue(formatEnterpriseText(currentInvoice.Enterprise)),
                },
                { label: "Статус", value: formatEmptyValue(formatBoStateText(currentInvoice.State)) },
            ],
        };
    }, []);

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: invoice,
        confirmAndPerform,
        buildParams: buildBoOperationParams,
    });

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        if (operationItems.length === 0) {
            return [];
        }

        return [
            {
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную БО-операцию",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            },
        ];
    }, [operationItems]);

    const openContentWindow = useCallback(
        (contentCode: number): void => {
            stockFinishedGoodsShipmentCardWindows.content.open(windows, {
                contentCode,
                contents: contentsQuery.content,
                setConfirmLeaveMutation: mutations.setConfirmLeaveMutation,
            });
        },
        [contentsQuery.content, mutations.setConfirmLeaveMutation, windows],
    );

    if (cardQuery.isFetching || invoice == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="stock_finished_goods_shipment_card_page">
            <CardHeader
                text={`Отгрузка с СГП по НС №${invoice.Num}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            <DetailList
                className="stock_finished_goods_shipment_card_inform"
                layout="tiles"
                columns="auto"
                minWidth={96}
                oneLine
            >
                <DetailList.Row label="Статус НС" value={<BusinessBoStateValue state={invoice.State} />} />
                <DetailList.Row label="Склад" value={<BusinessDivisionValue division={invoice.WsSender} />} />
                <DetailList.Row
                    label="Контрагент-получатель"
                    value={<BusinessEnterpriseValue enterprise={invoice.Enterprise} />}
                />
            </DetailList>
            <StockFinishedGoodsShipmentContentsList contentsQuery={contentsQuery} onOpenContent={openContentWindow} />
            <StockFinishedGoodsShipmentScanPlaceholder
                contents={contentsQuery.content}
                enabled={!contentsQuery.isFetching && !isCardScannerSuppressedByWindow}
                onOpenContent={openContentWindow}
            />
        </div>
    );
};

function formatEmptyValue(value: string): string {
    return value === "" ? "—" : value;
}
